import Guid from 'es/core/types/Guid';
import { action, makeObservable, observable } from 'mobx';

interface TEntity {
    id: Guid | string;
}

export default class EntityStore<T extends TEntity> {
    constructor() {
        makeObservable<EntityStore<T>, 'entities'>(this, {
            reset: action.bound,
            add: action.bound,
            addOrUpdate: action.bound,
            remove: action.bound,
            entities: observable,
        });
    }

    protected entities: T[] = [];

    public reset(entities: T[]) {
        this.entities = entities;
    }

    public add(entityToAdd: T, afterId?: Guid | string) {
        const nextEntities: T[] = [];

        this.entities.forEach(entity => {
            nextEntities.push(entity);

            if (afterId && entity.id === afterId) {
                nextEntities.push(entityToAdd);
            }
        });

        if (this.entities.length === nextEntities.length) {
            nextEntities.push(entityToAdd);
        }

        this.entities = nextEntities;
    }

    public addOrUpdate(id: Guid | string, entity: T | ((prev: T | null) => T)) {
        const getReplacement = (prev: T | null) => (typeof entity === 'function' ? entity(prev) : entity);

        let hasReplaced = false;

        const next = this.entities.map(prev => {
            if (prev.id !== id) return prev;

            hasReplaced = true;
            return getReplacement(prev);
        });

        if (!hasReplaced) {
            next.push(getReplacement(null));
        }

        this.entities = next;
    }

    public findById = (id: Guid | string) => this.entities.find(entity => entity.id === id);

    public remove(id: Guid | string) {
        this.entities = this.entities.filter(entity => entity.id !== id);
    }
}
